import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { X, Plus, Trash2, PackageMinus } from "lucide-react";
import { NeuButton, NeuInput } from "../../components/neu/Neu";
import { NeuDropdown } from "../../components/data/NeuDropdown";
import { searchItems } from "../../api/masterItems";
import { createIssue, listWarehouses } from "../../api/warehouse";
import { localName } from "../../lib/localName";

// شاشة صرف مواد (شاشة كاملة): ترويسة برتقالية ثابتة + جسم قابل للتمرير.
export const IssueForm = ({ branchId, onClose, onDone }) => {
  const { t } = useTranslation();
  const [warehouses, setWarehouses] = useState([]);
  const [wh, setWh] = useState("");
  const [issuedTo, setIssuedTo] = useState("");
  const [lines, setLines] = useState([]);
  const [term, setTerm] = useState("");
  const [suggests, setSuggests] = useState([]);

  useEffect(() => { listWarehouses(branchId).then((d) => {
    setWarehouses(d.items || []); setWh((d.items || [])[0]?.warehouse_id || "");
  }); }, [branchId]);

  useEffect(() => { if (!term) { setSuggests([]); return; }
    searchItems(term, branchId, 10).then(setSuggests).catch(() => setSuggests([]));
  }, [term, branchId]);

  const addLine = (item) => { setLines([...lines, { item_id: item.item_id, item_code: item.item_code,
    item_name: item.name_ar, uom: item.unit, qty: 1, unit_cost: item.reference_price || 0 }]);
    setTerm(""); setSuggests([]); };

  const save = async () => {
    try { await createIssue({ branch_id: branchId, warehouse_id: wh,
      date: new Date().toISOString().slice(0,10), issued_to: issuedTo || null, lines });
      toast.success(t("warehouse.actions.issueSaved")); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail?.code || t("errors.generic")); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-background" data-testid="issue-form">
      <div className="flex flex-shrink-0 items-center justify-between gap-3 bg-gradient-to-r from-orange-500 to-orange-600 px-6 pb-4 pt-[max(1rem,calc(env(safe-area-inset-top)+0.75rem))] text-white shadow-xl">
        <div className="flex items-center gap-3">
          <PackageMinus className="h-6 w-6" />
          <p className="text-lg font-black sm:text-xl">{t("warehouse.actions.issue")}</p>
        </div>
        <button onClick={onClose} data-testid="iss-close"
                className="rounded-full bg-white/15 p-2 transition hover:bg-white/25">
          <X className="h-6 w-6" /></button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-8">
        <div className="mx-auto grid max-w-5xl gap-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <NeuDropdown testid="iss-wh" value={wh} onChange={setWh} placeholder={t("warehouse.bal.warehouse")}
                         options={warehouses.map((w) => ({ value: w.warehouse_id, label: localName(w) }))} />
            <NeuInput data-testid="iss-to" value={issuedTo} placeholder={t("warehouse.doc.issuedTo")}
                      onChange={(e) => setIssuedTo(e.target.value)} />
          </div>
          <div className="relative">
            <NeuInput data-testid="iss-search" value={term} placeholder={t("warehouse.actions.searchToAdd")}
                      onChange={(e) => setTerm(e.target.value)} />
            {suggests.length ? (
              <div className="absolute z-50 mt-1 w-full rounded-2xl bg-background p-2 shadow-neu">
                {suggests.map((s) => (
                  <button key={s.item_id} className="flex w-full items-center justify-between p-2 text-xs hover:bg-orange-500/10"
                          onClick={() => addLine(s)}>
                    <span className="font-bold">{localName(s)}</span>
                    <span className="num font-mono text-[10px] text-muted-foreground">{s.item_code}</span>
                  </button>))}</div>) : null}
          </div>
          <div className="grid gap-2 min-h-[200px]">
            {lines.length === 0 ? <p className="rounded-2xl bg-primary/5 p-10 text-center text-xs text-muted-foreground">
                {t("warehouse.actions.searchToAdd")}</p>
              : lines.map((l, i) => (
              <div key={i} className="grid grid-cols-6 items-center gap-2 rounded-2xl bg-orange-500/5 p-2 text-xs">
                <span className="col-span-2 font-bold">{l.item_name}</span>
                <span className="num font-mono">{l.item_code}</span>
                <NeuInput dir="ltr" data-testid={`iss-qty-${i}`} value={l.qty} type="number"
                          onChange={(e) => setLines(lines.map((x,ix) => ix===i ? {...x, qty: Number(e.target.value)||0} : x))} />
                <span className="num font-mono text-muted-foreground">{l.unit_cost}</span>
                <button onClick={() => setLines(lines.filter((_,ix) => ix!==i))}><Trash2 className="h-4 w-4 text-rose-500" /></button>
              </div>))}
          </div>
          <div className="flex justify-end border-t border-border/40 pt-4">
            <NeuButton variant="primary" data-testid="iss-save" disabled={!wh || !lines.length} onClick={save}
                       className="bg-orange-500 text-white hover:bg-orange-600">
              <Plus className="inline h-4 w-4" /> {t("warehouse.actions.saveIssue")}</NeuButton>
          </div>
        </div>
      </div>
    </div>);
};
